import { ProgramBenchmarkName, ProgramTask } from '@cibo/core'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { Button, Popover } from '@mui/material'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useProgramEngagementContext } from '../../../../../context'
import { SingleTargetSchemaEditor } from '../../../Tasks/DataEntry/SingleTargetSchemaEditor'
import { TemplateEditSingleFieldForm } from './TemplateEditSingleFieldForm'

interface TemplateTraitEditorPopoverProps {
  anchorEl: HTMLElement | null
  onClose: () => void
  traitId: string
  year: number
  currentValue: any
  task: ProgramTask<{ benchmarkName: ProgramBenchmarkName }>
  onSave: (filledValues: { value?: unknown; input?: unknown; result?: unknown }) => void
  onDelete?: () => void
}

export const TemplateTraitEditorPopover = ({
  anchorEl,
  onClose,
  traitId,
  year,
  currentValue,
  task,
  onSave,
  onDelete,
}: TemplateTraitEditorPopoverProps) => {
  const { t } = useTranslation('@cibo/programs/TemplateEditorModal')

  const { taskData, program } = useProgramEngagementContext()

  const traitStatus = taskData.data?.getTraitStatus(traitId)

  const benchmarkRequirements = program.data?.benchmarks?.find(
    b => b.benchmark === task.rule?.params?.benchmarkName
  )?.details

  const requirement = benchmarkRequirements?.find(
    (req: any) => req.traitId === traitId && req.year === year
  )

  const DeleteButton = onDelete ? (
    <Button
      style={{ width: '100%' }}
      variant="outlined"
      onClick={onDelete}
      color="error"
      startIcon={<DeleteOutlineIcon />}
    >
      {t('removePractice')}
    </Button>
  ) : undefined

  const renderContent = () => {
    const detailValueKey = traitStatus?.detailValueKey ?? 'result'

    if (traitStatus?.schema) {
      return (
        <SingleTargetSchemaEditor
          traitStatus={traitStatus}
          handleSaveTraitDetail={({ value }) => {
            onSave({ [detailValueKey]: value })
          }}
          traitDetailValue={currentValue?.[detailValueKey]}
          year={year}
          variant="compact"
          fieldWrapperProps={{ p: 2 }}
          buttonWrapperProps={{ sx: { background: 'background.paper' }, spacing: 2, p: 2, pt: 0 }}
          submitButtonProps={{ style: { width: '100%', marginTop: 16 }, variant: 'contained' }}
          DeleteButton={DeleteButton}
        />
      )
    }

    return (
      <TemplateEditSingleFieldForm
        traitId={traitId}
        year={year}
        requirement={requirement}
        currentValue={currentValue}
        onSave={onSave}
        requestClose={onClose}
        DeleteButton={DeleteButton}
      />
    )
  }

  const maxPaperHeight = useMemo(() => {
    if (!anchorEl) return undefined
    const { bottom } = anchorEl.getBoundingClientRect()
    return Math.max(window.innerHeight - bottom - 16, 100)
  }, [anchorEl])

  return (
    <Popover
      disableScrollLock
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      slotProps={{ paper: { sx: { maxWidth: 300, maxHeight: maxPaperHeight, overflowY: 'auto' } } }}
    >
      {renderContent()}
    </Popover>
  )
}
